import { ref, computed } from 'vue';
import { useStorage } from '@vueuse/core';
import axios from 'axios';
import { clearContractDrafts } from '@/composables/useContractDraft';
import { clearSalesCrmFilterSessions } from '@/composables/salesCrmFilterStorage';
// import { axiosAuthInstance } from '@/services/auth.interceptor';
const api = axios.create({
  baseURL: process.env.VUE_APP_SERVER_AUTH_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isRememberMeChecked = useStorage('isRememberMeChecked', false, localStorage);

const isV1AuthEnabled = () => (
  process.env.NODE_ENV === 'development'
  && localStorage.getItem('qbeek:authApiVersion') === 'v1'
  && Boolean(process.env.VUE_APP_AUTH_REGION)
);

export const isCrmRole = (value) => typeof value === 'string' && value.startsWith('crm_');

export const isTransientAuthError = (cause) => {
  const status = cause?.response?.status;
  return status === 429 || (status >= 500 && status < 600) || (cause?.isAxiosError && !cause.response);
};

const getAuthFlow = ({ contexts, merchants }) => {
  if (isCrmRole(contexts?.crm?.role)) {
    if (merchants?.length) {
      console.warn('Auth response has both CRM context and merchants');
    }
    return 'crm';
  }

  return merchants?.length ? 'merchant' : 'personal';
};

function createDualStorage(key, initialValue) {
  const local = useStorage(key, initialValue, localStorage);
  const session = useStorage(key, initialValue, sessionStorage);

  const value = computed({
    get: () => {
      if (isRememberMeChecked.value) {
        return local.value;
      }
      return session.value;
    },
    set: (val) => {
      if (isRememberMeChecked.value) {
        local.value = val;
      } else {
        session.value = val;
      }
    }
  });

  return { local, session, value };
}

const _accessToken = createDualStorage('accessToken', '');
const _refreshToken = createDualStorage('refreshToken', '');
const _tokenExpiresAt = createDualStorage('tokenExpiresAt', 0);
const _merchantId = createDualStorage('merchantId', '');
const _merchants = createDualStorage('merchants', []);
const _user = createDualStorage('user', null);
const _uid = createDualStorage('uid', null);
const _role = createDualStorage('role', null);

const accessToken = _accessToken.value;
const refreshToken = _refreshToken.value;
const tokenExpiresAt = _tokenExpiresAt.value;
const merchantId = _merchantId.value;
const merchants = _merchants.value;
const user = _user.value;
const uid = _uid.value;
const role = _role.value;

const isLoading = ref(false);
const error = ref(null);
let refreshAccessTokenPromise = null;

const clearLocalAuthState = () => {
  clearContractDrafts();
  clearSalesCrmFilterSessions(_uid.local.value);
  clearSalesCrmFilterSessions(_uid.session.value);
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }

  _accessToken.local.value = '';
  _accessToken.session.value = '';
  _refreshToken.local.value = '';
  _refreshToken.session.value = '';
  _merchantId.local.value = '';
  _merchantId.session.value = '';
  _tokenExpiresAt.local.value = 0;
  _tokenExpiresAt.session.value = 0;
  _merchants.local.value = [];
  _merchants.session.value = [];
  _user.local.value = null;
  _user.session.value = null;
  _uid.local.value = null;
  _uid.session.value = null;
  _role.local.value = null;
  _role.session.value = null;
};

// Set up auto refresh before token expires
let refreshTimeout = null;
const setupAutoRefresh = (expiresAt) => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
  if (!expiresAt) return;
  const timeoutMs = expiresAt - Date.now() - 60 * 1000; // Refresh 1 minute before expiry
  // console.log('setupAutoRefresh:expiresAt', timeoutMs);

  if (timeoutMs > 0) {
    refreshTimeout = setTimeout(async () => {
      await refreshAccessToken();
    }, timeoutMs);
  }
};

const logout = async () => {
  const tokenToRevoke = accessToken.value;
  clearLocalAuthState();

  try {
    if (tokenToRevoke) {
      await api.post('/api/token/revoke', {
        accessToken: tokenToRevoke,
      });
    }
  } catch (err) {
    console.error('Error revoking token:', err);
  }
};

const refreshAccessTokenInternal = async () => {
  try {
    // console.group("refreshAccessToken");
    // console.log('refreshToken.value', refreshToken.value);
    // console.log('merchantId.value', merchantId.value);

    if (!refreshToken.value) {
      await logout();
      return false;
    }

    if (isV1AuthEnabled()) {
      const isCrm = isCrmRole(role.value);
      if (!isCrm && !merchantId.value) {
        await logout();
        return false;
      }

      const response = await api.post(
        `/api/v1/${process.env.VUE_APP_AUTH_REGION}/token/refresh`,
        isCrm
          ? { refreshToken: refreshToken.value, context: 'crm' }
          : { refreshToken: refreshToken.value, merchantId: merchantId.value },
        { baseURL: '' },
      );
      const authData = response.data?.data || response.data;

      if (response.data?.success === false || !authData?.accessToken) {
        throw response.data?.message || 'Не удалось обновить сессию';
      }

      accessToken.value = authData.accessToken;
      if (authData.refreshToken) refreshToken.value = authData.refreshToken;
      tokenExpiresAt.value = authData.expiresAt || 0;
      setupAutoRefresh(authData.expiresAt || 0);
      return true;
    }

    const isCrm = isCrmRole(role.value);
    if (!isCrm && !merchantId.value) {
      await logout();
      return false;
    }

    const response = await api.post('/api/token/refresh', isCrm
      ? { refreshToken: refreshToken.value, context: 'crm' }
      : { refreshToken: refreshToken.value, merchantId: merchantId.value });

    if (!response.data?.accessToken) {
      throw new Error('Не удалось обновить сессию');
    }

    accessToken.value = response.data.accessToken;
    if (response.data.refreshToken) {
      refreshToken.value = response.data.refreshToken;
    }
    tokenExpiresAt.value = response.data.expiresAt;
    setupAutoRefresh(response.data?.expiresAt || 0);
    return true;
  } catch (err) {
    if (!isTransientAuthError(err)) await logout();
    console.error('Error refreshing token:', err?.response?.status || err?.code || 'unknown');
    error.value = isTransientAuthError(err)
      ? 'Сервис авторизации временно недоступен. Повторите попытку позже.'
      : 'Session expired. Please login again.';
    throw err;
  } finally {
    console.groupEnd();
  }
};

const refreshAccessToken = async () => {
  if (!refreshAccessTokenPromise) {
    refreshAccessTokenPromise = refreshAccessTokenInternal().finally(() => {
      refreshAccessTokenPromise = null;
    });
  }

  return refreshAccessTokenPromise;
};

const validateToken = async () => {
  try {
    if (!accessToken.value) {
      await logout();
      return false;
    }

    if (isV1AuthEnabled()) {
      const response = await api.post(
        `/api/v1/${process.env.VUE_APP_AUTH_REGION}/token/validate`,
        { accessToken: accessToken.value },
        { baseURL: '' },
      );
      const authData = response.data?.data || response.data;

      if (response.data?.success === false || !authData) {
        await logout();
        return false;
      }

      user.value = JSON.stringify(authData);
      uid.value = authData.userId || uid.value;
      role.value = authData.crmRole || authData.role || role.value;
      return true;
    }

    const response = await api.post('/api/token/validate', {
      accessToken: accessToken.value,
    });

    if (!response.data) {
      await logout();
      return false;
    }
    
    // Update user info in the active storage
    user.value = JSON.stringify(response.data);
    uid.value = response.data.userId;
    role.value = response.data.crmRole || response.data.role || null;
    
    return true;
  } catch (err) {
    await logout();
    throw err;
  }
};

const switchMerchant = async (newMerchantId) => {
  try {
    merchantId.value = newMerchantId;
    await refreshAccessToken();
    return true;
  } catch (err) {
    error.value = 'Failed to switch merchant';
    throw error.value;
  }
};

// Onboarding-сессия: у пользователя уже есть credentials (refresh-токен), но ещё нет организации,
// поэтому access-токена нет. Хранится только в sessionStorage вкладки, remember-me отключается.
const acceptOnboardingAuth = ({ userId, refreshToken: nextRefreshToken, merchants: nextMerchants = [] }) => {
  isRememberMeChecked.value = false;
  clearLocalAuthState();
  _refreshToken.session.value = nextRefreshToken;
  _merchants.session.value = nextMerchants;
  _uid.session.value = userId;
};

// После `POST /applications/current/complete`: подключаем созданную организацию к сессии
// и получаем access-токен под неё.
const completeOnboardingAuth = async (merchant) => {
  if (!merchant?.id || !refreshToken.value) {
    error.value = 'Не удалось активировать созданную организацию';
    throw error.value;
  }

  merchants.value = [...merchants.value.filter((item) => item.id !== merchant.id), merchant];
  merchantId.value = merchant.id;
  await refreshAccessToken();
  await validateToken();
  return true;
};

const login = async ({ email, password }, isRememberMe = false) => {
  let loginRequestSucceeded = false;
  let isV1Auth = false;

  try {
    isLoading.value = true;
    error.value = null;

    isV1Auth = isV1AuthEnabled();

    const response = await api.post(
      isV1Auth ? `/api/v1/${process.env.VUE_APP_AUTH_REGION}/login/` : '/api/login',
      {
        email,
        password,
      },
      {
        baseURL: isV1Auth
          ? ''
          : process.env.VUE_APP_SERVER_AUTH_URL,
      },
    );

    if (isV1Auth && response.data?.success !== true) {
      throw response.data?.message || 'Проверьте свой логин или пароль';
    }

    loginRequestSucceeded = true;
    const authData = isV1Auth ? response.data.data : response.data;
    const authMerchants = isV1Auth ? authData.merchants || [] : authData.merchants;

    // Handle Storage Selection based on Remember Me
    isRememberMeChecked.value = isRememberMe;
    
    // Clear both storages first to ensure clean state
    _refreshToken.local.value = '';
    _refreshToken.session.value = '';
    _merchants.local.value = [];
    _merchants.session.value = [];
    _tokenExpiresAt.local.value = 0;
    _tokenExpiresAt.session.value = 0;
    _merchantId.local.value = '';
    _merchantId.session.value = '';
    _accessToken.local.value = '';
    _accessToken.session.value = '';

    if (isRememberMe) {
      // Set to local storage
      _refreshToken.local.value = authData.refreshToken;
      _merchants.local.value = authMerchants;
      _tokenExpiresAt.local.value = authData.expiresAt;
      _merchantId.local.value = authMerchants[0]?.id || '';
    } else {
      // Set to session storage
      _refreshToken.session.value = authData.refreshToken;
      _merchants.session.value = authMerchants;
      _tokenExpiresAt.session.value = authData.expiresAt;
      _merchantId.session.value = authMerchants[0]?.id || '';
    }

    uid.value = authData.userId || null;
    role.value = authData.contexts?.crm?.role || null;

    if (isV1Auth || isCrmRole(role.value)) {
      const flow = getAuthFlow(authData);
      if (flow === 'crm') {
        role.value = authData.contexts.crm.role;
      }

      if (flow !== 'personal') {
        await refreshAccessToken();
        await validateToken();
        return { flow };
      }

      return { pending: true, flow, canStartOnboarding: authMerchants.length === 0 };
    }

    // If merchants exist, set the first one as default
    if (merchants.value.length > 0) {
      // Use the computed setter which should route to the correct storage now that one is set
      merchantId.value = merchants.value[0].id;
      
      // Get initial access token using refresh token and merchant ID
      await refreshAccessToken();
      await validateToken();
    } else if (authData.contexts?.personal?.canStartOnboarding !== false) {
      // Credentials есть, организации нет — незавершённая регистрация (resume).
      return { flow: 'personal', canStartOnboarding: true };
    }

    return true;
  } catch (err) {
    if (isV1Auth && typeof err === 'string') {
      error.value = err;
      throw err;
    }

    const status = err.response?.status;
    const cause = err.response?.data?.cause;
    const isMerchantBlocked = status === 423 || (
      status === 500
      && Number(cause?.status) === 423
      && cause?.code === 'MERCHANT_BLOCKED'
    );

    if (isMerchantBlocked || (loginRequestSucceeded && status === 401)) {
      error.value = 'login.errors.accessRestricted';
      throw error.value;
    }

    if (isTransientAuthError(err)) {
      error.value = 'login.errors.serviceUnavailable';
      throw error.value;
    }

    error.value = err.response?.data?.message || 'Проверьте свой логин или пароль';
    throw error.value;
  } finally {
    isLoading.value = false;
  }
};

export function useAuth() {
  const isAuthenticated = computed(() => !!accessToken.value);
  
  const isTokenExpired = () => {
    return Boolean(tokenExpiresAt.value) && Date.now() >= tokenExpiresAt.value;
  };

  return {
    accessToken,
    merchantId,
    merchants,
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    validateToken,
    refreshAccessToken,
    switchMerchant,
    acceptOnboardingAuth,
    completeOnboardingAuth,
    isTokenExpired,
    uid,
    userId: uid,
    role,
  };
}
